import { motion } from "framer-motion";
import { ServerCog, SlidersHorizontal, Palette, MonitorSmartphone, ArrowDown } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const NODES = [
  { icon: ServerCog, title: "RCS Core Platform", text: "A proven, reusable product foundation — built once, hardened over time." },
  { icon: SlidersHorizontal, title: "Client-Specific Configuration", text: "Modules, roles and rules tuned to how your organization actually works." },
  { icon: Palette, title: "Branding + Features + Workflows", text: "Your logo, your colors, your domain, your processes — not a generic template." },
  { icon: MonitorSmartphone, title: "Your Website / SaaS / Mobile App", text: "A digital product that looks and works like it was built only for you." },
];

export const ArchitectureFlow = () => (
  <section id="saas-model" className="grain grid-lines-dark relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="saas-model-section">
    <div className="pointer-events-none absolute right-[10%] top-[-10%] h-[380px] w-[380px] rounded-full bg-gold/[0.08] blur-[120px]" />
    <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <Eyebrow dark>How We Build</Eyebrow>
        <h2 className="mt-5 font-jakarta text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="saas-model-heading">
          Built Once. Customized for Your Business.
        </h2>
        <p className="mt-5 font-inter text-[15px] leading-relaxed text-slate-400">
          RCS can build a reusable core platform and customize the experience for each organization with its own
          branding, domain, users, workflows, features and data environment.
        </p>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 gap-4 lg:grid-cols-4">
        {NODES.map((n, i) => (
          <Reveal key={n.title} delay={i * 0.12}>
            <div className="relative h-full" data-testid={`saas-flow-node-${i + 1}`}>
              <div className="group h-full rounded-xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur transition-[border-color,background-color] duration-300 hover:border-gold/40 hover:bg-white/[0.07]">
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-gold/30 bg-gold/10 text-gold-light">
                    <n.icon className="h-5 w-5" />
                  </span>
                  <span className="font-mono2 text-[11px] tracking-widest text-slate-600">0{i + 1}</span>
                </div>
                <h3 className="mt-5 font-jakarta text-[15.5px] font-bold leading-snug text-white">{n.title}</h3>
                <p className="mt-2 font-inter text-[12.5px] leading-relaxed text-slate-400">{n.text}</p>
              </div>
              {i < NODES.length - 1 && (
                <motion.span
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.12 }}
                  className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-gold lg:-right-3.5 lg:bottom-auto lg:left-auto lg:top-1/2 lg:-translate-y-1/2 lg:translate-x-0 lg:rotate-[-90deg]"
                  aria-hidden="true"
                >
                  <ArrowDown className="h-4 w-4" />
                </motion.span>
              )}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
